// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { useUser } from '@/app/user-context';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import {
  formatMeritLadder,
  getDisplayMeritLadder,
  isAchievementMeritGame,
  isPerEventMeritGame,
  useGameScoringRules,
} from '@/features/game-definitions/merits';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { card } from '../../typography';
import GameTile from '@/features/game-selection/GameTile';
import GroupedGameGrid from '@/features/game-selection/GroupedGameGrid';
import MeritDistributionBar from '@/features/game-selection/MeritDistributionBar';
import { cycleWeightPercent, normalizeWeight } from '@/features/game-selection/weights';
import { resolveSelectedRuleSetId } from '@/features/game-selection/ruleSets';
import SectionHeading from '../shared/SectionHeading';
import { TOUR_EDIT_MEDIA_PAGE_PATH } from '../../constants';
import type { GameDefinition } from '../../types';
import { normalizeGameId } from '../../utils';
import { isTournamentOnlyGame } from '@/features/game-definitions/tournamentOnly';

type Props = {
  roundId: string;
  enabledGameDefinitions: GameDefinition[];
  roundGameIds: string[];
  weightsByGameId: Record<string, number>;
  ruleSetByGameId?: Record<string, string>;
  tournamentGameIds: string[];
  tournamentWeightsByGameId: Record<string, number>;
  tournamentRuleSetByGameId?: Record<string, string>;
  playerCount: number;
  onUpdateRoundGames: (roundId: string, gameIds: string[]) => void;
  onWeightChange: (roundId: string, gameId: string, weightPercent: number) => void;
  onRuleSetChange?: (roundId: string, gameId: string, ruleSetId: string) => void;
};

const FALLBACK_MERIT_PLAYER_COUNT = 4;

export default function RoundGamesEditor({
  roundId,
  enabledGameDefinitions: allGameDefinitions,
  roundGameIds,
  weightsByGameId,
  ruleSetByGameId,
  tournamentGameIds,
  tournamentWeightsByGameId,
  tournamentRuleSetByGameId,
  playerCount,
  onUpdateRoundGames,
  onWeightChange,
  onRuleSetChange,
}: Props) {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { user } = useUser();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);

  // Tour-only games (Eclectic, Freeze) are not round games; they are managed in
  // the tour games editor and never appear here, not even as locked tiles.
  const enabledGameDefinitions = useMemo(
    () => allGameDefinitions.filter((game) => !isTournamentOnlyGame(game)),
    [allGameDefinitions],
  );

  const roundGameSet = useMemo(() => new Set(roundGameIds.map(normalizeGameId)), [roundGameIds]);
  const tournamentGameSet = useMemo(
    () => new Set(tournamentGameIds.map(normalizeGameId)),
    [tournamentGameIds],
  );
  const meritPlayerCount = playerCount > 1 ? playerCount : FALLBACK_MERIT_PLAYER_COUNT;
  const scoringPointsByGameId = useGameScoringRules(apiBaseUrl, user?.token, meritPlayerCount);

  const getWeightPercent = useCallback(
    (game: GameDefinition) => {
      if (tournamentGameSet.has(normalizeGameId(game.id))) {
        return tournamentWeightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent);
      }
      return weightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent);
    },
    [tournamentGameSet, tournamentWeightsByGameId, weightsByGameId],
  );

  const cycleWeight = useCallback(
    (game: GameDefinition) => {
      if (tournamentGameSet.has(normalizeGameId(game.id))) return;
      onWeightChange(roundId, game.id, cycleWeightPercent(getWeightPercent(game)));
    },
    [getWeightPercent, onWeightChange, roundId, tournamentGameSet],
  );

  const meritDistributionEntries = useMemo(
    () =>
      enabledGameDefinitions
        .filter((game) => {
          const normalized = normalizeGameId(game.id);
          return roundGameSet.has(normalized) || tournamentGameSet.has(normalized);
        })
        .map((game) => ({
          gameId: game.id,
          name: getGameDisplayName(game),
          ladder: getDisplayMeritLadder(scoringPointsByGameId, game.id, getWeightPercent(game), game.name, game.gameType),
          paysPerEvent: isPerEventMeritGame(game.gameType),
        })),
    [enabledGameDefinitions, getWeightPercent, roundGameSet, scoringPointsByGameId, tournamentGameSet],
  );

  const handleToggle = useCallback(
    (gameId: string) => {
      const normalizedId = normalizeGameId(gameId);
      if (tournamentGameSet.has(normalizedId)) return;

      const currentRoundOnly = roundGameIds.filter((id) => !tournamentGameSet.has(normalizeGameId(id)));
      if (roundGameSet.has(normalizedId)) {
        onUpdateRoundGames(
          roundId,
          currentRoundOnly.filter((id) => normalizeGameId(id) !== normalizedId),
        );
      } else {
        onUpdateRoundGames(roundId, [...currentRoundOnly, gameId]);
      }
    },
    [roundId, roundGameIds, roundGameSet, tournamentGameSet, onUpdateRoundGames],
  );

  const openInfo = useCallback((game: GameDefinition) => {
    setInfoGame(game);
    setInfoOpen(true);
  }, []);

  const renderTile = useCallback(
    (game: GameDefinition) => {
      const normalized = normalizeGameId(game.id);
      const isTourGame = tournamentGameSet.has(normalized);
      const isSelected = isTourGame || roundGameSet.has(normalized);
      const weightPercent = getWeightPercent(game);
      const ladder = isSelected
        ? getDisplayMeritLadder(scoringPointsByGameId, game.id, weightPercent, game.name, game.gameType)
        : [];
      return (
        <GameTile
          key={game.id}
          game={game}
          selected={isSelected}
          mediaPagePath={TOUR_EDIT_MEDIA_PAGE_PATH}
          locked={isTourGame}
          lockedLabel={isTourGame ? 'Tour' : undefined}
          onPress={() => handleToggle(game.id)}
          onInfoPress={() => openInfo(game)}
          meritLadderLabel={ladder.length > 0 ? formatMeritLadder(ladder) : undefined}
          perTeam={(game.competitionType ?? '').toLowerCase() === 'team'}
          perAchievement={isAchievementMeritGame(game.name, game.gameType)}
          perEvent={isPerEventMeritGame(game.gameType)}
          weightPercent={weightPercent}
          onWeightPress={() => cycleWeight(game)}
          weightLocked={isTourGame}
        />
      );
    },
    [
      cycleWeight,
      getWeightPercent,
      handleToggle,
      openInfo,
      roundGameSet,
      scoringPointsByGameId,
      tournamentGameSet,
    ],
  );

  return (
    <View style={styles.container}>
      <SectionHeading title="Round Games" count={roundGameIds.length} />
      <Text style={styles.hint}>
        Tour games are auto-included and cannot be removed. Additional games can be toggled.
      </Text>
      <MeritDistributionBar entries={meritDistributionEntries} playerCount={meritPlayerCount} />
      {enabledGameDefinitions.length === 0 ? (
        <Text style={styles.emptyText}>No games available</Text>
      ) : (
        <GroupedGameGrid games={enabledGameDefinitions} renderTile={renderTile} />
      )}
      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={() => setInfoOpen(false)}
        selectedRuleSetId={
          infoGame
            ? resolveSelectedRuleSetId({
                ruleSets: infoGame.rules,
                roundRuleSetId: ruleSetByGameId?.[infoGame.id],
                tourRuleSetId: tournamentRuleSetByGameId?.[infoGame.id],
              })
            : null
        }
        tourRuleSetId={infoGame ? tournamentRuleSetByGameId?.[infoGame.id] : null}
        // Deliberately offered for tour-locked games too, unlike the weight control: a tour game's
        // weight is tour-managed, but its rule set is only a tour *default* the round may override.
        onSelectRuleSet={
          infoGame && onRuleSetChange ? (ruleSetId) => onRuleSetChange(roundId, infoGame.id, ruleSetId) : undefined
        }
        ruleScope="round"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...card,
    gap: 8,
  },
  hint: {
    fontSize: 12,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingHorizontal: 4,
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
